import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import getExportPoa from '../../services/poa/export_poa';

// ─── Helpers de fecha ────────────────────────────────────────────────────────

const MESES_ES = ['ENR', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
                  'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

/**
 * Convierte { year: 2024, month: 3 } → "valor_2024_03"
 */
const periodoToKey = (year, month) =>
    `valor_${year}_${String(month).padStart(2, '0')}`;

/**
 * Convierte { year: 2024, month: 3 } → "MAR-24"
 */
const periodoToLabel = (year, month) =>
    `${MESES_ES[month - 1]}-${String(year).slice(2)}`;

/**
 * Convierte un índice de columna (1-based) a letra(s) de Excel: 1→A, 27→AA
 */
const colIndexToLetter = (n) => {
    let result = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        result = String.fromCharCode(65 + rem) + result;
        n = Math.floor((n - 1) / 26);
    }
    return result;
};

// ─── Componente ──────────────────────────────────────────────────────────────

const ExportPoa = () => {
    const [dataAislamiento,   setDataAislamiento]   = useState([]);
    const [dataProductos,     setDataProductos]     = useState([]);
    const [dataEjecucion,     setDataEjecucion]     = useState([]);
    const [dataAdministrativa,setDataAdministrativa]= useState([]);
    const [periodos,          setPeriodos]          = useState([]); // [{ year, month }, ...]
    const [loading,           setLoading]           = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getExportPoa();

                const filteredDataAislamiento  = response.data.filter(item => item.oid_fase === 1);
                const filteredDataEjecucion    = response.data.filter(item => item.oid_fase === 2);
                const filteredDataAdministrativa = response.data.filter(item => item.oid_fase === 3);
                const uniqueProducts = [...new Set(filteredDataEjecucion.map(item => item.producto))];

                setDataAislamiento(filteredDataAislamiento);
                setDataEjecucion(filteredDataEjecucion);
                setDataAdministrativa(filteredDataAdministrativa);
                setDataProductos(uniqueProducts);
                setPeriodos(response.periodos || []);
            } catch (error) {
                console.error('Error fetching file tree:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // ─── Utilidades de fusión de celdas ──────────────────────────────────────

    const combinarCeldasEnParesDeFilas = (worksheet, startRow, endRow, column) => {
        for (let row = startRow; row < endRow; row += 2) {
            worksheet.mergeCells(row, column, row + 1, column);
            const mergedCell = worksheet.getCell(row, column);
            mergedCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        }
    };

    const combinarCeldasaPorcentage = (worksheet, startRow, endRow, column) => {
        for (let row = startRow; row < endRow; row += 2) {
            const rawCelda1 = worksheet.getCell(row,     column - 1).value;
            const rawCelda2 = worksheet.getCell(row + 1, column - 1).value;

            // Normalizar a número aunque vengan como "12.5%" (tarea 2.1.4)
            const num1 = parseFloat(String(rawCelda1).replace('%', ''));
            const num2 = parseFloat(String(rawCelda2).replace('%', ''));

            let porcentajeAvance = null;

            if (!isNaN(num2) && !isNaN(num1) && num1 !== 0) {
                porcentajeAvance = parseFloat(((num2 * 100) / num1).toFixed(1));
                worksheet.getCell(row,     column).value = porcentajeAvance + '%';
                worksheet.getCell(row + 1, column).value = null;
            }

            worksheet.mergeCells(row, column, row + 1, column);

            const val = porcentajeAvance ?? 0;
            const colorAvance = val < 30  ? 'FF0100'
                              : val < 100 ? 'FFC001'
                              : '92D050';

            const mergedCell = worksheet.getCell(row, column);
            mergedCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            mergedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colorAvance } };
        }
    };

    const combinarCeldasRepetidasEnRango = (worksheet, startRow, endRow, column = 1) => {
        let currentValue      = worksheet.getCell(startRow, column).value;
        let currentMergeStart = startRow;
        let contador221       = 1;

        for (let row = startRow + 1; row <= endRow; row++) {
            const cellValue    = worksheet.getCell(row, column).value;
            const codigoTarea  = worksheet.getCell(row, 2).value;

            if (column === 47 && codigoTarea === '2.2.1') {
                if (currentMergeStart < row - 1) {
                    worksheet.mergeCells(currentMergeStart, column, row - 1, column);
                    worksheet.getCell(currentMergeStart, column).alignment =
                        { vertical: 'middle', horizontal: 'center', wrapText: true };
                }
                if (row % 2 === 1) {
                    try {
                        worksheet.mergeCells(row, column, row + 1, column);
                        const mergedCell  = worksheet.getCell(row, column);
                        mergedCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                        mergedCell.value  = `https://bodecom.com/poa/2.2.1.${contador221}`;
                        contador221++;
                    } catch (_) {}
                }
                currentMergeStart = row;
                currentValue      = cellValue;
                continue;
            }

            if (cellValue !== currentValue) {
                if (currentMergeStart < row - 1) {
                    worksheet.mergeCells(currentMergeStart, column, row - 1, column);
                    worksheet.getCell(currentMergeStart, column).alignment =
                        { vertical: 'middle', horizontal: 'center', wrapText: true };
                }
                currentMergeStart = row;
                currentValue      = cellValue;
            }
        }

        if (currentMergeStart < endRow) {
            const lastCodigoTarea = worksheet.getCell(endRow, 2).value;
            if (!(column === 47 && lastCodigoTarea === '2.2.1')) {
                worksheet.mergeCells(currentMergeStart, column, endRow, column);
                worksheet.getCell(currentMergeStart, column).alignment =
                    { vertical: 'middle', horizontal: 'center', wrapText: true };
            }
        }
    };

    // ─── Escritura de filas de datos ─────────────────────────────────────────

    /**
     * Escribe una fila de datos en el worksheet de forma dinámica.
     * Las columnas de meses se infieren de `periodos` (vienen del backend).
     *
     * Layout fijo:
     *   Col 1  → actividad
     *   Col 2  → codigo_tarea
     *   Col 3  → tarea
     *   Col 4  → indicador
     *   Col 5  → meta_proyectada
     *   Col 6  → tipo (P/E)
     *   Col 7  → primer período
     *   ...
     *   Col 6+N → último período          (N = periodos.length)
     *   Col 6+N+1 → suma seguimiento      (= colSeguimiento)
     *   Col 6+N+2 → % avance              (= colAvance)
     *   Col 6+N+3 → medio de verificación (= colMedio)
     *   Col 6+N+4 → link                  (= colLink)
     */
    const escribirFila = (row, item, periodos, formatValue = null) => {
        const fmt = formatValue || ((v) => v ?? '');

        row.getCell(1).value = item.actividad       || '';
        row.getCell(2).value = item.codigo_tarea    || '';
        row.getCell(3).value = item.tarea           || '';
        row.getCell(4).value = item.indicador       || '';
        row.getCell(5).value = fmt(item.meta_proyectada);
        row.getCell(6).value = item.tipo            || '';

        // Columnas de meses: dinámicas
        const colMesInicio = 7;
        periodos.forEach(({ year, month }, i) => {
            const key = periodoToKey(year, month);
            row.getCell(colMesInicio + i).value = fmt(item[key]);
        });

        const colSeguimiento = colMesInicio + periodos.length;      // suma
        const colAvance      = colSeguimiento + 1;                  // %
        const colMedio       = colAvance      + 1;
        const colLink        = colMedio       + 1;
        const colFin         = colLink;

        // Suma de meses
        let suma_total = 0;
        for (let i = 0; i < periodos.length; i++) {
            const val = row.getCell(colMesInicio + i).value;
            const num = parseFloat(String(val).replace('%', ''));
            if (!isNaN(num)) suma_total += num;
        }
        const esPorcentaje = item.codigo_tarea === '2.1.4' ? '%' : '';
        row.getCell(colSeguimiento).value = suma_total + esPorcentaje || 0;

        row.getCell(colMedio).value = item.medio_verificacion || '';
        row.getCell(colLink).value  = 'https://bodecom.com/poa/' + item.codigo_tarea;

        // Color fondo por tipo (solo columnas que no sean % AVANCE,
        // cuyo color lo calcula combinarCeldasaPorcentage después del merge)
        const colorTipo = item.tipo === 'P' ? 'D9E3F3' : null;
        if (colorTipo) {
            for (let col = 6; col <= colFin; col++) {
                if (col === colAvance) continue; // lo gestiona combinarCeldasaPorcentage
                row.getCell(col).fill = {
                    type: 'pattern', pattern: 'solid',
                    fgColor: { argb: colorTipo }
                };
            }
        }

        return { colSeguimiento, colAvance, colMedio, colLink, colFin };
    };

    // ─── Exportar Excel ───────────────────────────────────────────────────────

    const exportarData = () => {
        const workbook  = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('POA');

        // ── Posiciones de columna calculadas dinámicamente ──
        const COL_MES_INICIO  = 7;
        const N               = periodos.length;
        const COL_SEGUIMIENTO = COL_MES_INICIO + N;       // suma P/E
        const COL_AVANCE      = COL_SEGUIMIENTO + 1;      // % avance
        const COL_MEDIO       = COL_AVANCE      + 1;      // medio verificación
        const COL_LINK        = COL_MEDIO       + 1;      // link
        const COL_FIN         = COL_LINK;

        const letraFin        = colIndexToLetter(COL_FIN);

        // ── Encabezados fijos (cols 1-6) ──
        worksheet.mergeCells('A1:A2'); worksheet.getCell('A1').value = 'ACTIVIDAD';
        worksheet.mergeCells('B1:B2'); worksheet.getCell('B1').value = 'No. TAREA';
        worksheet.mergeCells('C1:C2'); worksheet.getCell('C1').value = 'TAREAS';
        worksheet.mergeCells('D1:D2'); worksheet.getCell('D1').value = 'INDICADORES';
        worksheet.mergeCells('E1:E2'); worksheet.getCell('E1').value = 'META';
        worksheet.mergeCells('F1:F2'); worksheet.getCell('F1').value = 'P/E';

        // ── Encabezados de meses: dinámicos ──
        // Agrupa los periodos por año para generar los merge de la fila 1
        const porAnio = periodos.reduce((acc, p, i) => {
            if (!acc[p.year]) acc[p.year] = { startIdx: i, endIdx: i };
            else acc[p.year].endIdx = i;
            return acc;
        }, {});

        Object.entries(porAnio).forEach(([year, { startIdx, endIdx }]) => {
            const colStart = COL_MES_INICIO + startIdx;
            const colEnd   = COL_MES_INICIO + endIdx;
            const letStart = colIndexToLetter(colStart);
            const letEnd   = colIndexToLetter(colEnd);

            if (colStart === colEnd) {
                worksheet.getCell(`${letStart}1`).value = year;
            } else {
                worksheet.mergeCells(`${letStart}1:${letEnd}1`);
                worksheet.getCell(`${letStart}1`).value = year;
            }

            // Etiquetas de mes en fila 2
            for (let i = startIdx; i <= endIdx; i++) {
                const { year: y, month: m } = periodos[i];
                const letra = colIndexToLetter(COL_MES_INICIO + i);
                worksheet.getCell(`${letra}2`).value = periodoToLabel(y, m);
            }
        });

        // ── Encabezados de seguimiento / avance / medio / link ──
        const letSeg   = colIndexToLetter(COL_SEGUIMIENTO);
        const letAva   = colIndexToLetter(COL_AVANCE);
        const letMedio = colIndexToLetter(COL_MEDIO);
        const letLink  = colIndexToLetter(COL_LINK);

        worksheet.mergeCells(`${letSeg}1:${letAva}1`);
        worksheet.getCell(`${letSeg}1`).value = 'SEGUIMIENTO CORTE A LA FECHA DE ACTUALIZACIÓN';
        worksheet.getCell(`${letSeg}2`).value = 'P. A LA FECHA / E. A LA FECHA';
        worksheet.getCell(`${letAva}2`).value = '% AVANCE';

        worksheet.mergeCells(`${letMedio}1:${letMedio}2`);
        worksheet.getCell(`${letMedio}1`).value = 'MEDIO DE VERIFICACIÓN';

        worksheet.mergeCells(`${letLink}1:${letLink}2`);
        worksheet.getCell(`${letLink}1`).value = 'LINK';

        // ── Estilos encabezado (filas 1-2) ──
        [1, 2].forEach((fila) => {
            worksheet.getRow(fila).eachCell({ includeEmpty: true }, (cell) => {
                cell.font      = { color: { argb: 'FFFFFFFF' }, bold: true };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2F5496' } };
            });
        });

        // Helper para el merge del título de sección
        const escribirTituloSeccion = (fila, texto, color = '757070') => {
            worksheet.mergeCells(`A${fila}:${letraFin}${fila}`);
            const cell = worksheet.getCell(`A${fila}`);
            cell.value     = texto;
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
            cell.font      = { color: { argb: 'FFFFFF' }, bold: true };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            worksheet.getRow(fila).height = 30;
        };

        // Helper para fusionar y aplicar estilos a una sección de datos
        const aplicarFusionesSeccion = (startRow, endRow) => {
            [1, 2, 3, 4].forEach(col =>
                combinarCeldasRepetidasEnRango(worksheet, startRow, endRow, col));
            combinarCeldasEnParesDeFilas(worksheet,    startRow, endRow, 5);
            combinarCeldasaPorcentage(worksheet,       startRow, endRow, COL_AVANCE);
            combinarCeldasRepetidasEnRango(worksheet,  startRow, endRow, COL_MEDIO);
            combinarCeldasRepetidasEnRango(worksheet,  startRow, endRow, COL_LINK);
        };

        // ══════════════════════════════════════════════════════════════════════
        // FASE 1 – Alistamiento
        // ══════════════════════════════════════════════════════════════════════
        const FILA_FASE1_TITULO = 3;
        escribirTituloSeccion(FILA_FASE1_TITULO, 'Alistamiento');

        const FILA_FASE1_INICIO = FILA_FASE1_TITULO + 1;
        dataAislamiento.forEach((item, index) => {
            const row = worksheet.getRow(FILA_FASE1_INICIO + index);
            escribirFila(row, item, periodos);
        });

        const FILA_FASE1_FIN = FILA_FASE1_INICIO + dataAislamiento.length - 1;
        aplicarFusionesSeccion(FILA_FASE1_INICIO, FILA_FASE1_FIN);

        // ══════════════════════════════════════════════════════════════════════
        // FASE 2 – Ejecución
        // ══════════════════════════════════════════════════════════════════════
        const FILA_FASE2_TITULO = FILA_FASE1_FIN + 1;
        escribirTituloSeccion(FILA_FASE2_TITULO, 'Ejecución');

        let filaEjecucion = FILA_FASE2_TITULO + 1;

        dataProductos.forEach((product) => {
            const dataPorProducto = dataEjecucion.filter(item => item.producto === product);

            // Título del producto
            worksheet.mergeCells(`A${filaEjecucion}:F${filaEjecucion}`);
            const cellProducto = worksheet.getCell(`A${filaEjecucion}`);
            cellProducto.value     = `Producto: ${product}`;
            cellProducto.font      = { bold: false };
            cellProducto.alignment = { horizontal: 'center' };
            worksheet.getRow(filaEjecucion).height = 60;

            for (let col = 1; col <= COL_FIN; col++) {
                const cell = worksheet.getRow(filaEjecucion).getCell(col);
                cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: '8EAADB' } };
                cell.alignment = { vertical: 'middle', wrapText: true };
            }

            const filaProductoInicio = filaEjecucion + 1;

            dataPorProducto.forEach((item, index) => {
                const row = worksheet.getRow(filaProductoInicio + index);

                // formatValue especial para tarea 2.1.4
                const formatValue = (value) => {
                    if (item.codigo_tarea !== '2.1.4') return value ?? '';
                    if (value === null || value === undefined || value === '' || value === 0 || value === '0')
                        return value ?? '';
                    return `${value}%`;
                };

                escribirFila(row, item, periodos, formatValue);
            });

            const filaProductoFin = filaProductoInicio + dataPorProducto.length - 1;
            aplicarFusionesSeccion(filaProductoInicio, filaProductoFin);

            filaEjecucion += dataPorProducto.length + 1;
        });

        // ══════════════════════════════════════════════════════════════════════
        // FASE 3 – Actividades Administrativas
        // ══════════════════════════════════════════════════════════════════════
        const FILA_FASE3_TITULO = filaEjecucion;
        escribirTituloSeccion(FILA_FASE3_TITULO,
            'Actividades administrativas - Aspectos transversales del Proyecto');

        const FILA_FASE3_INICIO = FILA_FASE3_TITULO + 1;
        dataAdministrativa.forEach((item, index) => {
            const row = worksheet.getRow(FILA_FASE3_INICIO + index);
            escribirFila(row, item, periodos);
        });

        const FILA_FASE3_FIN = FILA_FASE3_INICIO + dataAdministrativa.length - 1;
        aplicarFusionesSeccion(FILA_FASE3_INICIO, FILA_FASE3_FIN);

        // ══════════════════════════════════════════════════════════════════════
        // Estilos globales de columna
        // ══════════════════════════════════════════════════════════════════════
        worksheet.getColumn(1).width       = 40;
        worksheet.getColumn(3).width       = 40;
        worksheet.getColumn(4).width       = 40;
        worksheet.getColumn(COL_MEDIO).width = 30;
        worksheet.getColumn(COL_LINK).width  = 30;

        // Bordes en todas las celdas
        worksheet.eachRow({ includeEmpty: true }, (row) => {
            row.eachCell((cell) => {
                cell.border = {
                    top:    { style: 'thin' },
                    left:   { style: 'thin' },
                    bottom: { style: 'thin' },
                    right:  { style: 'thin' },
                };
            });
        });

        // ── Descargar ──
        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const link     = document.createElement('a');
            link.href      = URL.createObjectURL(blob);
            link.download  = 'Poa_UTF_COL_160.xlsx';
            link.click();
        });
    };

    return (
        <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportarData}
            shape="circle"
            size="large"
            loading={loading}
        />
    );
};

export default ExportPoa;